import React, { createContext, useMemo, useCallback, useContext, useState } from 'react';
import Toast, { ToastAction, ToastProps, ToastStatus } from '../components/ui/Toast';

interface ToastItem extends ToastProps {
  id: string;
}

interface ShowOptions {
  description?: string;
  duration?: number;
  /** Ação secundária (ex.: "Desfazer") renderizada dentro do toast. */
  action?: ToastAction;
}

interface ToastContextValue {
  show: (title: string, status: ToastStatus, options?: ShowOptions) => string;
  success: (title: string, description?: string, duration?: number) => string;
  error: (title: string, description?: string, duration?: number) => string;
  warning: (title: string, description?: string, duration?: number) => string;
  info: (title: string, description?: string, duration?: number) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Provider global de toasts — mantém a fila de toasts ativos e expõe o hook
 * useToast() para qualquer componente da árvore, sem precisar de prop drilling
 * nem de instanciar useToast() localmente (que não persiste entre desmontagens).
 *
 * Substitui o padrão de alert()/window.confirm() informativo por feedback
 * visual consistente com a marca (ver src/components/ui/Toast.tsx).
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const show = useCallback((title: string, status: ToastStatus, options: ShowOptions = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setToasts(prev => [...prev, {
      id,
      title,
      status,
      description: options.description,
      duration: options.duration,
      action: options.action,
    }]);
    return id;
  }, []);

  const success = useCallback((title: string, description?: string, duration?: number) =>
    show(title, 'success', { description, duration }), [show]);
  const error = useCallback((title: string, description?: string, duration?: number) =>
    show(title, 'error', { description, duration }), [show]);
  const warning = useCallback((title: string, description?: string, duration?: number) =>
    show(title, 'warning', { description, duration }), [show]);
  const info = useCallback((title: string, description?: string, duration?: number) =>
    show(title, 'info', { description, duration }), [show]);

  // As funções são estáveis: quem só dispara toast não redesenha quando a fila muda.
  const value = useMemo(
    () => ({ show, success, error, warning, info, dismiss }),
    [show, success, error, warning, info, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast, index) => (
        <Toast key={toast.id} {...toast} offsetIndex={index} onClose={() => dismiss(toast.id)} />
      ))}
    </ToastContext.Provider>
  );
};

/**
 * Hook para disparar toasts de qualquer componente dentro do <ToastProvider>.
 * Uso: const toast = useToast(); toast.success('Salvo com sucesso');
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de um <ToastProvider>');
  }
  return ctx;
}
